// front-end/multimodal/src/StoryListing.tsx
import React, { useState, useEffect, KeyboardEvent } from 'react';
import './StoryListing.css';
import { getStorageJSON, formatDate } from './utils'; // Added formatDate import

// Interface for the data structure fetched from stories_listing.json
interface StoryListItem {
  path: string;
  title: string;
  summary: string;
  created_at: string; // Assuming it's a date string
  sentences: number; // Assuming this is the count
  sentencesLength: number; // Assuming this is words/sentence
  // Add other potential fields if they exist and are used for sorting/display
}

// Props for the StoryListing component
interface StoryListingProps {
  listingUri: string;
  onStorySelection: (path: string) => void;
  focusStory: string | null;
}

// Sort state interface
type SortDirection = 'asc' | 'desc' | null;
interface SortState {
  column: keyof StoryListItem | null;
  direction: SortDirection;
}

// Arrow component props
interface ArrowProps {
  column: keyof StoryListItem;
}

const StoryListing: React.FC<StoryListingProps> = ({ listingUri, onStorySelection, focusStory }) => {
  const domainMatch = listingUri.match(/^(https?:\/\/[^/]+|[^/]+)/);
  const domain = domainMatch ? domainMatch[0] + "/" : "/";
  const initialSort = getStorageJSON<SortState>("sort", { column: null, direction: null });

  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [sort, _setSort] = useState<SortState>(initialSort);

  // Persist to local storage using JSON
  const setSort = (newSort: SortState) => {
    _setSort(newSort);
    localStorage.setItem('sort', JSON.stringify(newSort));
  }

  // Show up/down arrow next to column header if it's sorted
  const Arrow: React.FC<ArrowProps> = ({ column }) => {
    if (sort.column !== column) return null;
    return <>{sort.direction === 'asc' ? '\u25B2' : sort.direction === 'desc' ? '\u25BC' : ''}</>;
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch(listingUri);
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        const data = await res.json() as StoryListItem[]; // Assert fetch result type
        setStories(data);
      } catch (error) {
        console.error('Error fetching stories:', error);
      }
    }
    fetchData();
  }, [listingUri]);

  const handleHeaderClick = (column: keyof StoryListItem) => {
    const order: SortDirection[] = ["asc", "desc", null];
    if (sort.column === column) {
      setSort({ column, direction: order[(order.indexOf(sort.direction) + 1) % order.length] });
    } else {
      setSort({ column, direction: order[0] });
    }
  };

  // Type sort function parameters
  const sortDate = (a: StoryListItem, b: StoryListItem): number => {
    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
    // Handle invalid dates
    if (isNaN(dateA.getTime())) return 1; 
    if (isNaN(dateB.getTime())) return -1;
    return dateA.getTime() - dateB.getTime();
  };

  const sortedStories = sort.direction === null ? [...stories] : [...stories].sort((a, b) => {
    if (sort.column === 'created_at') {
      return sort.direction === 'asc' ? sortDate(a, b) : sortDate(b, a);
    }
    // Ensure sort.column is not null before indexing
    if (sort.column) {
      const valA = a[sort.column];
      const valB = b[sort.column];
      if (valA < valB) {
        return sort.direction === 'asc' ? -1 : 1;
      }
      if (valA > valB) {
        return sort.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  // Define column structure type
  interface ColumnDefinition {
    name: keyof StoryListItem | 'thumbnail'; // Allow 'thumbnail' specifically
    label: string;
    sortable: boolean;
    className: string;
  }

  const columns: ColumnDefinition[] = [
    {name: 'thumbnail', label: '', sortable: false, className: 'thumbnail-cell'},
    {name: 'title', label: 'Title', sortable: true, className: 'title-cell'},
    {name: 'summary', label: 'Summary', sortable: true, className: 'summary-cell'},
    {name: 'created_at', label: 'Created', sortable: true, className: 'created-cell'},
    {name: 'sentences', label: 'Sentences', sortable: true, className: 'sentences-cell'},
    {name: 'sentencesLength', label: 'Words/s.', sortable: true, className: 'words-per-sentence-cell'},
  ];

  return sortedStories.length === 0 ? "Loading..." : (    
    <table>
      <thead>
        <tr>{columns.map(({ name, label, sortable, className }) => (
            sortable ? (
              <th
                key={name}
                tabIndex={0}
                onClick={() => handleHeaderClick(name as keyof StoryListItem)}
                onKeyDown={(e: KeyboardEvent<HTMLTableHeaderCellElement>) => {
                  if (e.key === 'Enter') handleHeaderClick(name as keyof StoryListItem);
                }}
                className={className.replace('-cell', '-header')}
              >
                {label} <Arrow column={name as keyof StoryListItem} />
              </th>
            ) : (
              <th key={name} className={`${className} ${className.replace('-cell', '-header')} non-sortable`}>{label}</th>
            )
          ))}</tr>
      </thead>

      <tbody>
        {sortedStories.map((story, index) => (
          <tr key={index}>
            {columns.map(({ name, className }) => (
              <td key={name} className={className}>
                {name === 'thumbnail' ? (
                  <div className="thumbnail-placeholder">
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a href="#" role="button" onClick={() => onStorySelection(story.path)} tabIndex={0}>
                      <img src={`${domain}lang/th/stories/${story.path}/thumbnail.webp`} loading="lazy" alt={story.title} />
                    </a>                  
                  </div>
                ) : name === 'title' ? (
                  <button autoFocus={focusStory === story.path} onClick={() => onStorySelection(story.path)}>
                    {story.title}
                  </button>
                ) : name === 'created_at' ? (
                  formatDate(story[name])
                ) : name === 'sentencesLength' ? (
                  story[name].toFixed(2)
                ) : (
                  story[name as keyof StoryListItem]
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default StoryListing;
